import { Effect } from 'effect';
import { PUBLIC_SIGNUP_CTA } from './signup-cta';

export const MARKETING_CTA_FLAG_KEY = 'marketing-primary-cta-v1';

export type MarketingCtaVariant = 'download' | 'sign_up';
export type MarketingCtaDecisionSource = 'posthog' | 'cookie' | 'fallback' | 'default';

export interface MarketingCtaBootstrap {
  variant: MarketingCtaVariant;
  decisionSource: MarketingCtaDecisionSource;
  distinctId?: string;
  enrolled: boolean;
}

export interface MarketingPrimaryCtaConfig extends MarketingCtaBootstrap {
  label: string;
  href: string;
}

declare global {
  interface Window {
    __OKRA_MARKETING_CTA__?: unknown;
  }
}

const CTA_BY_VARIANT: Record<
  MarketingCtaVariant,
  Pick<MarketingPrimaryCtaConfig, 'label' | 'href'>
> = {
  download: {
    label: 'Download desktop apps',
    href: '/download',
  },
  sign_up: PUBLIC_SIGNUP_CTA,
};

const DEFAULT_BOOTSTRAP: MarketingCtaBootstrap = {
  // Non-apex surfaces (for example app.okrapdf.com and local development)
  // retain the established account conversion action. The apex Worker always
  // injects either a PostHog assignment or the download fail-open decision.
  variant: 'sign_up',
  decisionSource: 'default',
  enrolled: false,
};

export function isMarketingCtaVariantEffect(value: unknown): Effect.Effect<boolean> {
  return Effect.sync(() => value === 'download' || value === 'sign_up');
}

export function isMarketingCtaVariant(value: unknown): value is MarketingCtaVariant {
  return Effect.runSync(isMarketingCtaVariantEffect(value));
}

function isDecisionSource(value: unknown): value is MarketingCtaDecisionSource {
  return value === 'posthog' || value === 'cookie' || value === 'fallback' || value === 'default';
}

function resolveMarketingCtaBootstrapInternal(value?: unknown): MarketingCtaBootstrap {
  const candidate =
    value ?? (typeof window !== 'undefined' ? window.__OKRA_MARKETING_CTA__ : undefined);

  if (!candidate || typeof candidate !== 'object') return DEFAULT_BOOTSTRAP;

  const record = candidate as Record<string, unknown>;
  if (!isMarketingCtaVariant(record.variant) || !isDecisionSource(record.decisionSource)) {
    return DEFAULT_BOOTSTRAP;
  }

  return {
    variant: record.variant,
    decisionSource: record.decisionSource,
    distinctId:
      typeof record.distinctId === 'string' && record.distinctId ? record.distinctId : undefined,
    enrolled: record.enrolled === true,
  };
}

export function resolveMarketingCtaBootstrapEffect(
  value?: unknown,
): Effect.Effect<MarketingCtaBootstrap> {
  return Effect.sync(() => resolveMarketingCtaBootstrapInternal(value));
}

export function resolveMarketingCtaBootstrap(value?: unknown): MarketingCtaBootstrap {
  return Effect.runSync(resolveMarketingCtaBootstrapEffect(value));
}

export const getMarketingPrimaryCtaEffect = Effect.fn('web.marketingCta.primary')(function* () {
  const bootstrap = yield* resolveMarketingCtaBootstrapEffect();
  return { ...bootstrap, ...CTA_BY_VARIANT[bootstrap.variant] };
});

export function getMarketingPrimaryCta(): MarketingPrimaryCtaConfig {
  return Effect.runSync(getMarketingPrimaryCtaEffect());
}

/**
 * The account action with no experiment assignment (W.4.104). The landing and
 * the desktop-surface header use it: their primary action is always Sign Up,
 * and the download action lives on the download page.
 */
export const getSignUpPrimaryCtaEffect = Effect.fn('web.marketingCta.signUp')(function* () {
  return { ...DEFAULT_BOOTSTRAP, ...CTA_BY_VARIANT.sign_up };
});

export function getSignUpPrimaryCta(): MarketingPrimaryCtaConfig {
  return Effect.runSync(getSignUpPrimaryCtaEffect());
}

export function marketingCtaAttributionEffect(
  cta: MarketingPrimaryCtaConfig,
): Effect.Effect<Record<string, unknown>> {
  return Effect.sync(() => ({
    cta_variant: cta.variant,
    cta_decision_source: cta.decisionSource,
    ...(cta.enrolled ? { [`$feature/${MARKETING_CTA_FLAG_KEY}`]: cta.variant } : {}),
  }));
}

export function marketingCtaAttribution(cta: MarketingPrimaryCtaConfig): Record<string, unknown> {
  return Effect.runSync(marketingCtaAttributionEffect(cta));
}
